import { useEffect } from 'react';
import { IsDemo } from 'core/context';
import AppContainer from 'edrolo/domains/app/components/AppContainer';
import {
  QuestionSetIndex,
  QuestionSet,
  QuestionSetTopBar,
} from 'edrolo/domains/exams';
import { useDemoQuestionResponse } from './hooks';
import LoadingIndicator from 'edrolo/domains/app/components/LoadingIndicator';
import urlResolver from 'edrolo/utils/URLResolver';
import { STATUS } from '../../constants';
import PT from 'prop-types';
import IPT from 'react-immutable-proptypes';

const QuestionSetDemoProvider = ({ children }) => {
  return <IsDemo.Provider value={{ isDemo: true }}>{children}</IsDemo.Provider>;
};

export const QuestionSetPageDemo = ({
  exam,
  examStatus,
  examId,
  questionId,
  classId,
  sections,
  numQuestions,
  questionsBySectionId,
  isTeacher,
  getQuestionInstructionText,
  questionResponses,
  question,
  rootQuestionDescendants,
  loadExam,
  searchParams,
  filterToggle,
  prevNextIds,
  history,
}) => {
  useEffect(() => {
    if (examStatus === STATUS.UNINITIALIZED) {
      loadExam(examId);
    }
  }, [examId, examStatus, loadExam]);

  const {
    data: response,
    submitQuestionResponse,
    submitChecklistReflectionItem,
    submitGrok,
    submitMark,
  } = useDemoQuestionResponse('irrelevant', questionId);

  const loading = examStatus !== STATUS.READY;

  const buildQuestionSetUrl = (examId, questionId, classId) => {
    const url = questionId
      ? urlResolver.reverse('demo-question-set-question', {
          examId,
          questionId,
        })
      : urlResolver.reverse('demo-question-set-index', {
          examId,
          classId,
        });

    return searchParams ? `${url}?${searchParams.toString()}` : url;
  };

  const analyticsPayload = {
    class_id: 'demo',
    exam_id: examId,
  };

  return (
    <QuestionSetDemoProvider>
      <AppContainer
        bodyClassName="lesson-questions"
        documentTitle="Edrolo Question Set Demo"
        renderTopBar={() => (
          <QuestionSetTopBar
            isTeacher={isTeacher}
            classId={classId}
            exam={exam}
            isDemo
          />
        )}
        renderLayout={(content) => (
          <AppContainer.BlankLayout>{content}</AppContainer.BlankLayout>
        )}
      >
        {!!loading && <LoadingIndicator loadingText="Loading QuestionSet" />}

        {!loading && !questionId && (
          <QuestionSetIndex
            examId={examId}
            exam={exam}
            classId={classId}
            sections={sections}
            numQuestions={numQuestions}
            questionsBySectionId={questionsBySectionId}
            isTeacher={isTeacher}
            getQuestionInstructionText={getQuestionInstructionText}
            questionResponses={questionResponses}
            buildQuestionSetUrl={buildQuestionSetUrl}
            searchParams={searchParams}
            filterToggle={filterToggle}
            analyticsPayload={analyticsPayload}
            schoolClassCode={exam.get('course_title')}
          />
        )}

        {!loading && question && (
          <QuestionSet
            classId={classId}
            examId={examId}
            questionId={questionId}
            sittingId={1} // unused in demo but set to avoid error
            exam={exam}
            question={question}
            response={
              response?.get('question') === parseInt(questionId)
                ? response
                : null
            }
            isTeacher={false}
            prevNextIds={prevNextIds}
            rootQuestionDescendants={rootQuestionDescendants}
            nextQuestionWhenSaved={prevNextIds.nextId}
            submitQuestionResponse={submitQuestionResponse}
            submitChecklistReflectionItem={submitChecklistReflectionItem}
            submitGrok={submitGrok}
            submitMark={submitMark}
            buildQuestionSetUrl={buildQuestionSetUrl}
            searchParams={searchParams}
            push={history.push}
          />
        )}
      </AppContainer>
    </QuestionSetDemoProvider>
  );
};

QuestionSetPageDemo.propTypes = {
  exam: IPT.map,
  examId: PT.string,
  classId: PT.oneOfType([PT.string, PT.number]).isRequired,
  numQuestions: PT.number,
  sections: IPT.list,
  questionsBySectionId: IPT.map,
  questionResponses: PT.any,
  isTeacher: PT.bool,
  getQuestionInstructionText: PT.func,
  searchParams: PT.object.isRequired,
  filterToggle: PT.shape({
    hasSpiciness: PT.bool.isRequired,
    hasWorkingPaths: PT.bool.isRequired,
  }).isRequired,
  questionId: PT.string,
  question: PT.object,
  rootQuestionDescendants: PT.any,
  examStatus: PT.string.isRequired,
  loadExam: PT.func.isRequired,
  prevNextIds: PT.shape({
    prevParentId: PT.number,
    nextParentId: PT.number,
    prevId: PT.number,
    nextId: PT.number,
  }).isRequired,
  history: PT.object.isRequired, // TODO: unknown
};
